import { Box, Chip, SxProps, Theme } from '@mui/material';
import { defaultSearchFilters, useQuoteListState } from 'shared/contexts/quotes-summary-context';
import { formatDateSlashed, replaceDashBySlash } from 'shared/utility';
import { useSessionStorage } from 'shared/hooks/use-session-storage';

const chipStyles: SxProps<Theme> = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 1,
};

const columnNames = {
  quoteNumber: 'Quote #',
  wholesalerCustomerNumber: 'Customer',
  jobInfoProjectName: 'Project Name',
  plumberProjectType: 'Project Type',
  wholesaler: 'Wholesaler',
  plumberContractor: 'Plumber',
  repAgency: 'Rep Agency',
  requestedBy: 'Rep',
  status: 'Status',
  brand: 'Brand',
  approvedBy: 'Approver',
  lastModifiedBy: 'Last Modified By',
  revisionApprovedBy: 'Revision Approver',
  jobInfoStateName: 'Job State',
  dateSubmitted: 'Date Submitted',
  dateRevised: 'Date Revised',
  dateApproved: 'Date Approved',
};

export function QuoteTableFilterChipBar() {
  const { quoteTableState, setQuoteTableState, setFormSearchFilters } = useQuoteListState();

  const setSearchFiltersSessionStorage = useSessionStorage('quoteSummaryFilters', defaultSearchFilters)[1];

  const isFilterDate = (filter: string): boolean =>
    filter === 'dateSubmitted' || filter === 'dateRevised' || filter === 'dateApproved';

  const handleDelete = (filter: string) => () => {
    setQuoteTableState(prevState => {
      const searchFilters = {
        ...prevState.searchFilters,
        [filter]: isFilterDate(filter) ? ['', ''] : '',
      };
      setSearchFiltersSessionStorage(searchFilters);
      return {
        ...prevState,
        ...searchFilters,
        tableState: {
          ...prevState.tableState,
          pageIndex: 1,
        },
      };
    });

    setFormSearchFilters({
      // The actual form and this search should be identical, but we can't change the context's
      // formSearch when typing or it will cause delayed rerenders. This will update the context's
      // formSearch to match the current search while also clearing out the selected search value.
      ...quoteTableState.searchFilters,
      [filter]: isFilterDate(filter) ? ['', ''] : '',
    });
  };

  const filters = Object.entries(quoteTableState.searchFilters);
  const isFilter = Object.values(quoteTableState.searchFilters)
    .map(filterValue => !!filterValue)
    .includes(true);

  const getValue = (columnValue: [string, any]) => {
    if (Array.isArray(columnValue)) {
      // verify if array exists and does not have empty strings
      if (!columnValue.some(c => !c)) {
        // new Date("10-06-2022") => 09-06-2022 & new Date("10/06/2022") => 10-06-2022
        // thats why we use replaceDashBySlash
        return (
          formatDateSlashed(new Date(replaceDashBySlash(columnValue[0]))) +
          ' - ' +
          formatDateSlashed(new Date(replaceDashBySlash(columnValue[1])))
        );
      }
      return;
    }

    return columnValue;
  };

  return isFilter ? (
    <Box sx={chipStyles} mb={1} mt={1}>
      {filters.map(column => {
        const filter = column[0];
        const correctFilterName = columnNames[filter as keyof typeof columnNames];

        const value = getValue(column[1]);

        return (
          value &&
          filter !== 'status' && (
            <Chip
              key={filter}
              label={
                <Box sx={chipStyles}>
                  <span>{correctFilterName}:</span>
                  {value}
                </Box>
              }
              onDelete={handleDelete(filter)}
              variant='outlined'
            />
          )
        );
      })}
    </Box>
  ) : null;
}
